import { Dir } from '@angular/cdk/bidi';
import { CdkOverlayOrigin } from '@angular/cdk/overlay';
import { CurrencyPipe, NgTemplateOutlet } from '@angular/common';
import { AfterViewInit, ChangeDetectionStrategy, Component, computed, ElementRef, inject, model, NgZone, OnDestroy, output, signal, viewChild, ViewEncapsulation } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatRippleModule } from '@angular/material/core';
import { MatDividerModule } from '@angular/material/divider';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuTrigger } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';
import { StTextAreaOverlayComponent } from '@common/controls/st-text-area/text-area-overlay/st-text-area-overlay';
import { Address } from '@common/data-models/complex-types/address';
import { PSTFieldNames } from '@common/data-models/fields/pst-field-names';
import { OPTION_BLANK } from '@common/enums/constants';
import { CardViewType, DragEventKeys, FieldDefValidations } from '@common/enums/enums';
import { selectCostFields, selectEnforceRequiredFieldsOnCreate, selectFieldDefsDict } from '@common/store/global-tables/global-tables.selectors';
import { selectViewStateCustomCardItems, selectViewStateSearchString } from '@common/store/navigation/selectors/viewstate.selectors';
import { LetDirective, PushPipe } from '@ngrx/component';
import { PlaybookService } from '@playbook/services/playbook-service';
import { environment } from '@src/environments/environment';
import { NavigationDirection } from '@src/suite/common/enums/navigation-direction';
import { StFullScreenComponent } from '@src/suite/common/st-full-screen-dialog/full-screen/full-screen';
import { FullscreenActions, OpenFullscreenModalActions, selectNewRecordCreationInProgress } from '@src/suite/common/store/fullscreen';
import { selectItemLayoutState } from '@src/suite/common/store/fullscreen/selectors/fullscreen.selectors';
import { selectTranslatedKey } from '@src/suite/common/store/i18n/selectors/i18n.selectors';
import { InlineFilterActions } from '@src/suite/common/store/inline-filter/inline-filter.actions';
import { enrichedConfigKey, FILTER_SECTION_ALL, FilterObjectTypes, FilterSavedState, FilterSections } from '@src/suite/common/store/inline-filter/inline-filter.models';
import { selectInlineFilterFeature } from '@src/suite/common/store/inline-filter/inline-filter.selectors';
import { selectCurrentNodeUuid } from '@src/suite/common/store/list/list.selectors';
import { PlaybookApiActions } from '@src/suite/common/store/playbook/playbook.actions';
import { RecordHttpActions, TopicRecordActions } from '@src/suite/common/store/records/records.actions';
import { selectStoryTemplates } from '@src/suite/common/store/records/records.selectors';
import { toLuxonISODateTimeString, toLuxonRelativeDateString } from '@src/suite/common/utils/date-utils';
import { GridDragTypes } from '@src/suite/common/utils/grid-utils';
import { I18nKeys } from '@src/suite/common/utils/i18n.default';
import {
    calculateRequiredFieldGroups,
    checkRequiredProperties,
    getCostPrivsRead,
    getCostPrivsWrite,
    getTopicSecurityPrivsWrite,
    getTypeInternal,
    isStoryInRundown,
} from '@src/suite/common/utils/record-utils';
import { applyFilter, buildReverseIndex, mergeFilters } from '@src/suite/common/utils/section-filter-utils';
import { getCardViewTypeForWidth, isStoryApproveField } from '@src/suite/common/utils/util';
import { AppService } from '@suite/app.service';
import { DateTime } from 'luxon';
import { fromEvent, Subject, takeUntil, tap } from 'rxjs';

import { LoaderComponent } from '../../../loader/loader';
import { ExternalActionsOverlayComponent } from '../../../playbook/components/external-actions-overlay/external-actions-overlay';
import { AssignmentCreationMenuComponent } from '../../components/assignment-creation-menu/assignment-creation-menu';
import { AssignmentComponent } from '../../components/assignments-list/assignment/assignment';
import { StAssignmentCondensedComponent } from '../../components/assignments-list/st-assignment-condensed/st-assignment-condensed';
import { AttachmentsListComponent } from '../../components/attachments-list/attachments-list';
import { BetaComponent } from '../../components/beta/st-beta.component';
import { StCondensedListHeaderLabelsComponent } from '../../components/condensed-list-labels/st-condensed-list-header-labels';
import { FilterTriggerComponent } from '../../components/filter/filter-trigger/filter-trigger';
import { StItemLayoutToggleComponent } from '../../components/item-layout-toggle/st-item-layout-toggle';
import { StoryItemComponent } from '../../components/stories-list/story-item/story-item';
import { StStoryItemCondensedComponent } from '../../components/stories-list/story-item-condensed/st-story-item-condensed';
import { StoryTemplateMenuComponent } from '../../components/story-template-menu/story-template-menu';
import { AddressFormComponent } from '../../controls/address-form/address-form';
import { CheckBoxComponent } from '../../controls/check-box/check-box';
import { CheckListComponent } from '../../controls/check-list/check-list';
import { ComboBoxComponent } from '../../controls/combo-box/combo-box';
import { DatePickerComponent } from '../../controls/date-picker/date-picker';
import { DateTimePickerComponent } from '../../controls/date-time-picker/date-time-picker';
import { DateTimeRangeComponent } from '../../controls/date-time-range/date-time-range';
import { DeskComponent } from '../../controls/desk/desk';
import { DurationSecondsInputFieldComponent } from '../../controls/duration-seconds-input-field/duration-seconds-input-field';
import { SpecialComponent } from '../../controls/special/special';
import { SpecialCostComponent } from '../../controls/special/special-cost/special-cost';
import { StTextAreaComponent } from '../../controls/st-text-area/st-text-area';
import { StTextAreaOverlayComponent as StTextAreaOverlayComponent_1 } from '../../controls/st-text-area/text-area-overlay/st-text-area-overlay';
import { StaffIDListComponent } from '../../controls/staff-id-list/staff-id-list';
import { StaffIDListMultipleComponent } from '../../controls/staff-id-list/staff-id-list-multiple';
import { StatusComponent } from '../../controls/status/status';
import { TagsAutocompleteComponent } from '../../controls/tags/tags-autocomplete';
import { TextFieldComponent } from '../../controls/text-field/text-field';
import { TopicTypeComponent } from '../../controls/topic-type/topic-type';
import { TreeComponent } from '../../controls/tree/tree';
import { ClickStopPropagationDirective } from '../../directives/stop-propagation';
import { ObjectTypes } from '../../enums/object-types';

@Component({
    selector: 'st-topic-full-screen',
    imports: [
        AddressFormComponent,
        AssignmentComponent,
        AssignmentCreationMenuComponent,
        AttachmentsListComponent,
        BetaComponent,
        CdkOverlayOrigin,
        CheckBoxComponent,
        CheckListComponent,
        ClickStopPropagationDirective,
        ComboBoxComponent,
        CurrencyPipe,
        DatePickerComponent,
        DateTimePickerComponent,
        DateTimeRangeComponent,
        DeskComponent,
        Dir,
        DurationSecondsInputFieldComponent,
        ExternalActionsOverlayComponent,
        FilterTriggerComponent,
        LetDirective,
        LoaderComponent,
        MatButtonModule,
        MatDividerModule,
        MatExpansionModule,
        MatIconModule,
        MatRippleModule,
        MatTooltipModule,
        NgTemplateOutlet,
        PushPipe,
        SpecialComponent,
        SpecialCostComponent,
        StaffIDListComponent,
        StaffIDListMultipleComponent,
        StAssignmentCondensedComponent,
        StatusComponent,
        StCondensedListHeaderLabelsComponent,
        StItemLayoutToggleComponent,
        StoryItemComponent,
        StoryTemplateMenuComponent,
        StStoryItemCondensedComponent,
        StTextAreaComponent,
        StTextAreaOverlayComponent_1,
        TagsAutocompleteComponent,
        TextFieldComponent,
        TopicTypeComponent,
        TreeComponent,
    ],
    templateUrl: './topic-full-screen.html',
    styleUrl: './topic-full-screen.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: {
        class: 'st-topic-full-screen',
    },
})
export class TopicFullScreenDialogComponent extends StFullScreenComponent implements AfterViewInit, OnDestroy {
    playbookService = inject(PlaybookService);
    appService = inject(AppService);
    ngZone = inject(NgZone);
    elmRef = inject(ElementRef);

    private containerResizeObserver: ResizeObserver;
    CardViewType = CardViewType;
    ObjectTypes = ObjectTypes;
    destroyed$ = new Subject<void>();

    readonly title = viewChild<StTextAreaOverlayComponent>('title');
    readonly storiesTrigger = viewChild<MatMenuTrigger>('storiesMenuTrigger');
    pstFieldNames = PSTFieldNames;
    fieldDefValidations = FieldDefValidations;
    NavigationDirection = NavigationDirection;
    environment = environment;
    readonly scrollableDiv = viewChild<ElementRef>('scrollableDiv');

    readonly customCardItems$ = this.store.select(selectViewStateCustomCardItems);
    readonly searchString$ = this.store.select(selectViewStateSearchString);
    readonly currentNodeUuid$ = this.store.select(selectCurrentNodeUuid);

    readonly storyTemplates = this.store.selectSignal(selectStoryTemplates);
    readonly enforceRequiredFieldsOnCreate = this.store.selectSignal(selectEnforceRequiredFieldsOnCreate);
    readonly itemLayoutState = this.store.selectSignal(selectItemLayoutState);

    readonly saveAndNext = signal(false);
    readonly showMatDivider = signal(false);
    private readonly currentWidth = signal(0);
    override readonly expandedGroup = signal<string>('TOPICITEM');
    override readonly data = model<StorytellingTopicRecord>();

    readonly topicOpenEmitter = output<void>();
    readonly storyOpen = output<StorytellingStoryRecord>();
    readonly assignmentOpen = output<StorytellingAssignmentRecord>();
    readonly discoverItemOpen = output<StorytellingRecord>();
    readonly storyDeleteEmitter = output<number>();

    readonly showAssignmentsApprove = computed(() => !!(this.store.selectSignal(selectFieldDefsDict)()?.[PSTFieldNames.PSTApproved] as FieldDef)?.AssignmentProp);
    readonly showStoriesApprove = computed(() => isStoryApproveField(this.store.selectSignal(selectFieldDefsDict)()?.[PSTFieldNames.Approved] as FieldDef));
    readonly loadingSaveAndNext = computed(() => {
        return this.store.selectSignal(selectNewRecordCreationInProgress)() && this.saveAndNext();
    });
    readonly loading = computed(() => {
        return this.store.selectSignal(selectNewRecordCreationInProgress)() && !this.saveAndNext();
    });
    readonly costFields = computed(() => {
        return this.store.selectSignal(selectCostFields)();
    });

    readonly topicEditable = computed(() => {
        return getTopicSecurityPrivsWrite(this.data()) && !this.data()?.Path?.isWasteBinPath;
    });

    readonly topicDisabled = computed(() => {
        return !this.topicEditable();
    });

    readonly cardDisabled = computed(() => {
        return !this.topicEditable() || this.data()?.Path?.isWasteBinPath;
    });

    readonly costEditable = computed(() => {
        return this.topicEditable() && getCostPrivsWrite(this.data());
    });

    readonly costDisabled = computed(() => {
        return !this.topicEditable() || !(this.appService.nomWebServicesJson?.HelloResults?.Supervisor || this.costEditable());
    });

    readonly costReadable = computed(() => {
        return getCostPrivsRead(this.data());
    });

    readonly costVisible = computed(() => {
        return this.appService.nomWebServicesJson?.HelloResults?.Supervisor || this.costReadable();
    });

    readonly isCreate = computed(() => {
        return this.data()?.isNewRecord;
    });

    readonly publicationDateLabel = computed(() => {
        return toLuxonISODateTimeString(this.data()?.PSTTargetDate);
    });

    readonly coverageAssignmentsFiedGroupLabel = computed(() => {
        const coverageAssignmentsFieldGroupLabel = 'PSTWebFieldGroup';

        return this.store
            .selectSignal(selectTranslatedKey(`${coverageAssignmentsFieldGroupLabel}_${I18nKeys.PSTWebCoverageAssignmentsPane}`))()
            .includes(coverageAssignmentsFieldGroupLabel)
            ? this.store.selectSignal(selectTranslatedKey(I18nKeys.PSTWebCoverageAssignmentsPane))()
            : this.store.selectSignal(selectTranslatedKey(`${coverageAssignmentsFieldGroupLabel}_${I18nKeys.PSTWebCoverageAssignmentsPane}`))();
    });

    readonly storiesFiedGroupLabel = computed(() => {
        const storiesFieldGroupLabel = 'PSTWebFieldGroup';
        return this.store
            .selectSignal(selectTranslatedKey(`${storiesFieldGroupLabel}_${I18nKeys.PSTWebStoriesPane}`))()
            .includes(storiesFieldGroupLabel)
            ? this.store.selectSignal(selectTranslatedKey(I18nKeys.PSTWebStoriesPane))()
            : this.store.selectSignal(selectTranslatedKey(`${storiesFieldGroupLabel}_${I18nKeys.PSTWebStoriesPane}`))();
    });

    readonly showAttachments = computed(() => {
        return this.appService.attachmentsAllowed && !!this.data()?.Guid && environment?.demoFlag;
    });

    readonly isRequiredFieldsPopulated = computed(() => {
        return checkRequiredProperties(this.data(), this.playbookService);
    });

    readonly areAssignmentsPopulated = computed(() => {
        if (this.data().children?.length) {
            for (const child of this.data().children) {
                if (!checkRequiredProperties(child, this.playbookService)) {
                    return false;
                }
            }
        }
        return true;
    });

    readonly areStoriesPopulated = computed(() => {
        if (this.data().stories?.length) {
            for (const story of this.data().stories) {
                if (!checkRequiredProperties(story, this.playbookService)) {
                    return false;
                }
            }
        }
        return true;
    });

    readonly requiredFieldGroups = computed(() => {
        return calculateRequiredFieldGroups(this.data(), this.playbookService);
    });

    readonly disabled = computed(() => {
        return !this.isRequiredFieldsPopulated() || !this.areAssignmentsPopulated() || !this.areStoriesPopulated() || this.loading() || this.loadingSaveAndNext();
    });

    override readonly getType = computed(() => {
        return getTypeInternal(this.data()?.Type);
    });

    readonly specialProps = computed(() => {
        return this.globalTablesService.getSpecialPropertiesFor(this.getType()).filter(field => field.ID !== PSTFieldNames.PSTExternalFiles && field.ID !== PSTFieldNames.Relationships);
    });

    readonly textAreaOverlayHighlight = computed(() => {
        return this?.data()?.isNewRecord ? '' : this.store.selectSignal(selectViewStateSearchString)();
    });

    readonly stories = computed<StorytellingStoryRecordIsDraggable[]>(() => {
        const stories = this.data()?.isNewRecord ? this.data()?.stories?.filter(story => !(story as StorytellingStoryRecord).PSTPrimaryStoryGUID) : this.data()?.stories;
        return (
            stories?.map(story => {
                return {
                    ...story,
                    isDraggable: !isStoryInRundown(story) && !this.data()?.isNewRecord,
                };
            }) || []
        );
    });

    readonly updatedBy = computed(() => {
        return `${this.store.selectSignal(selectTranslatedKey(I18nKeys.PSTWebChangedByLabel))()} ${this.globalTablesService.Staff[this?.data()?.ModBy]?.DisplayName}`;
    });

    readonly assignmentTypes = computed<LookupValue[]>(() => {
        return this.globalTablesService.getListItemsForField(this.appService.PSTFieldNames.DescriptionType);
    });

    private readonly FILTER_SECTION = FilterSections.TOPIC_FULL_SCREEN;
    private readonly filterFeatureState = this.store.selectSignal(selectInlineFilterFeature);

    readonly assignmentsFilterConfig = computed(() => {
        const configs = this.filterFeatureState()?.enrichedConfigs ?? {};
        return configs[enrichedConfigKey(FilterObjectTypes.ASSIGNMENT, this.FILTER_SECTION)] ?? configs[enrichedConfigKey(FilterObjectTypes.ASSIGNMENT, FILTER_SECTION_ALL)] ?? [];
    });
    readonly assignmentsFilterState = computed<FilterSavedState>(() => this.filterFeatureState()?.workingStates?.[FilterObjectTypes.ASSIGNMENT]?.[this.FILTER_SECTION] ?? {});
    readonly storiesFilterConfig = computed(() => {
        const configs = this.filterFeatureState()?.enrichedConfigs ?? {};
        return configs[enrichedConfigKey(FilterObjectTypes.STORY, this.FILTER_SECTION)] ?? configs[enrichedConfigKey(FilterObjectTypes.STORY, FILTER_SECTION_ALL)] ?? [];
    });
    readonly storiesFilterState = computed<FilterSavedState>(() => this.filterFeatureState()?.workingStates?.[FilterObjectTypes.STORY]?.[this.FILTER_SECTION] ?? {});
    readonly storyTemplateFilterConfig = computed(() => {
        const configs = this.filterFeatureState()?.enrichedConfigs ?? {};
        return configs[enrichedConfigKey(FilterObjectTypes.STORY_TEMPLATE, this.FILTER_SECTION)] ?? configs[enrichedConfigKey(FilterObjectTypes.STORY_TEMPLATE, FILTER_SECTION_ALL)] ?? [];
    });
    readonly storyTemplateFilterState = computed<FilterSavedState>(() => this.filterFeatureState()?.workingStates?.[FilterObjectTypes.STORY_TEMPLATE]?.[this.FILTER_SECTION] ?? {});

    private readonly assignmentsIndex = computed(() => buildReverseIndex(this.data()?.children ?? [], this.assignmentsFilterConfig()));
    private readonly storiesIndex = computed(() => buildReverseIndex(this.stories() ?? [], this.storiesFilterConfig()));

    private readonly assignmentsMerged = computed(() => mergeFilters(this.assignmentsFilterConfig(), this.assignmentsFilterState()));
    private readonly storiesMerged = computed(() => mergeFilters(this.storiesFilterConfig(), this.storiesFilterState()));

    readonly filteredAssignments = computed(() => applyFilter(this.data()?.children ?? [], this.assignmentsMerged(), this.assignmentsIndex()));
    readonly filteredStories = computed(() => applyFilter(this.stories() ?? [], this.storiesMerged(), this.storiesIndex()));

    readonly isRTL = computed(() => {
        return this.globalTablesService.getRtl();
    });

    readonly mosControlActiveIcon = computed(() => {
        return this.data()?.MOSControl ? 'mat-icon-topic-mos-active' : 'mat-icon-topic-mos-not-active';
    });

    readonly mosControlActiveTooltip = computed(() => {
        return this.data()?.MOSControl ? this.languageSettings()?.PSTWebTopicHoverDisableMOSControlActiveText : this.languageSettings()?.PSTWebTopicHoverEnableMOSControlActiveText;
    });

    readonly timeUpdatedLabel = computed(() => {
        return `${this.store.selectSignal(selectTranslatedKey(I18nKeys.PSTWebChangedAgoLabel))()} ${toLuxonRelativeDateString(this.data()?.ModTime, this.appService.updatedAgoSignal())}`;
    });

    readonly isAssignmentsCondensedLayout = computed(() => this.itemLayoutState().assignmentsFullscreen);
    readonly isStoriesCondensedLayout = computed(() => this.itemLayoutState().storiesFullscreen);

    readonly assignmentsCardViewType = computed(() => getCardViewTypeForWidth(this.currentWidth(), this.isAssignmentsCondensedLayout()));
    readonly storiesCardViewType = computed(() => getCardViewTypeForWidth(this.currentWidth(), this.isStoriesCondensedLayout()));

    ngAfterViewInit() {
        this.store.dispatch(PlaybookApiActions.loadStoryTemplates());
        if (this.isCreate()) {
            setTimeout(() => {
                this.title().onContainerClick();
            }, 500);
        }

        this.ngZone.runOutsideAngular(() => {
            fromEvent(this.scrollableDiv().nativeElement, 'scroll')
                .pipe(
                    tap((ev: Event) => {
                        if ((ev.target as HTMLElement).scrollTop > 0) {
                            this.showMatDivider.set(true);
                        } else {
                            this.showMatDivider.set(false);
                        }
                    }),
                    takeUntil(this.destroyed$)
                )
                .subscribe();
        });

        this.containerResizeObserver = new ResizeObserver(entries => {
            const width = entries?.[0]?.contentRect?.width;
            if (width) {
                this.currentWidth.set(width);
            }
        });

        this.containerResizeObserver?.observe(this.elmRef?.nativeElement);
    }

    ngOnDestroy(): void {
        this.destroyed$.next();
        this.destroyed$.complete();
        this.containerResizeObserver?.unobserve(this.elmRef?.nativeElement);
        this.containerResizeObserver?.disconnect();
    }

    getInnerProp(item: FieldDef) {
        const val = this.data()[item.ID.split('-')[0]];
        if (val) return val[item.ComplexPropInnerId];
        return '';
    }

    updateTopicAddresSubField(value: any, field: FieldDef, subFieldId: string) {
        if (!this.topicEditable()) return;
        const fieldId = field?.ID?.split('-')?.[0];
        if (fieldId) {
            const fieldDef = this.data()?.fieldDefsDict[fieldId];
            if (fieldDef?.Validation.toUpperCase() === FieldDefValidations.ADDRESS) {
                const address = this.data()?.[fieldId] || new Address();
                address[subFieldId] = value;
                this.updateTopic(address, fieldId);
            }
        }
    }

    onAssignmentDeleteHandler() {
        this.store.dispatch(FullscreenActions.updateCurrentRecord({ record: this.data() }));
    }

    onAssignmentOpenHandler(record: StorytellingAssignmentRecord) {
        this.assignmentOpen.emit(record);
    }

    onStoryOpenHandler(story: StorytellingStoryRecord) {
        this.storyOpen.emit(story);
    }

    onLinkedRecordOpenHandler(record: StorytellingRecord) {
        this.discoverItemOpen.emit(record);
    }

    updateTopic(value: any, fieldId: string) {
        if (!this.topicEditable()) return;

        if (fieldId === PSTFieldNames.Title) {
            value = value.trim();
            this.title().internalValue.set(value);
        }

        const fieldDef = [{ value, key: fieldId }];

        if (fieldId === PSTFieldNames.PSTTopicEventStartDate) {
            if ((value as DateTime) > this.data()?.EventEnd) {
                fieldDef.push({ value, key: PSTFieldNames.PSTTopicEventEndDate });
            }
        }
        this.store.dispatch(RecordHttpActions.saveRecordChanges({ record: this.data(), fieldsToUpdate: fieldDef }));
    }

    mosControlActive() {
        this.store.dispatch(PlaybookApiActions.mosControlActive({ guid: this.data().Guid }));
    }

    create(createAndNext: boolean) {
        if (!this.isRequiredFieldsPopulated()) return;
        this.saveAndNext.set(createAndNext);
        this.store.dispatch(PlaybookApiActions.prepareCreateTopic({ topic: this.data(), createAndNext }));
    }

    isGroupSingleValue(property: string): boolean {
        return this.globalTablesService.isGroupSingleValue && property === PSTFieldNames.PSTDesk;
    }

    // getList(group: string): FieldDef[] {
    //     // const list =
    //     // return list;
    // }

    onStoryTemplateMenuClosed(templates: StorytellingStoryRecord[]) {
        if (!templates?.length) {
            return;
        }
        let templateProgramId: string | null = null;
        const templateGuids: string[] = templates.map(template => template.Guid);
        templates
            .filter(template => template.Guid !== OPTION_BLANK)
            .forEach(template => {
                if (template && template.Path && template.Path.fullPath) {
                    templateProgramId = template.Path.fullPath;
                }
            });

        try {
            this.store.dispatch(PlaybookApiActions.createStoryFromTemplate({ topic: this.data(), templateProgramId: templateProgramId, templateGuids: templateGuids }));
        } catch (ex) {
            console.warn(ex);
        }
        this.panelStateChanged(true, this.storiesFiedGroupLabel());
    }

    onStoryTemplateFilterStateChange(state: FilterSavedState) {
        this.store.dispatch(InlineFilterActions.updateFilterState({ objectType: FilterObjectTypes.STORY_TEMPLATE, section: this.FILTER_SECTION, state }));
    }

    onStoryTemplateFilterSave() {
        this.store.dispatch(InlineFilterActions.saveFilterState({ objectType: FilterObjectTypes.STORY_TEMPLATE, section: this.FILTER_SECTION }));
    }

    onStoryTemplateFilterRevert() {
        this.store.dispatch(InlineFilterActions.revertFilterState({ objectType: FilterObjectTypes.STORY_TEMPLATE, section: this.FILTER_SECTION }));
    }

    onStoryTemplateFilterClear() {
        this.store.dispatch(InlineFilterActions.clearFilterState({ objectType: FilterObjectTypes.STORY_TEMPLATE, section: this.FILTER_SECTION }));
    }

    onAssignmentTypesSelected(types: string[]) {
        for (const type of types) {
            try {
                this.store.dispatch(PlaybookApiActions.createAssignment({ topic: this.data(), assignmentType: type }));
            } catch (ex) {
                console.warn(ex);
            }
        }
        this.panelStateChanged(true, this.coverageAssignmentsFiedGroupLabel());
    }

    onAssignmentsFilterStateChange(state: FilterSavedState) {
        this.store.dispatch(InlineFilterActions.updateFilterState({ objectType: FilterObjectTypes.ASSIGNMENT, section: this.FILTER_SECTION, state }));
    }

    onStoriesFilterStateChange(state: FilterSavedState) {
        this.store.dispatch(InlineFilterActions.updateFilterState({ objectType: FilterObjectTypes.STORY, section: this.FILTER_SECTION, state }));
    }

    onAssignmentsFilterSave() {
        this.store.dispatch(InlineFilterActions.saveFilterState({ objectType: FilterObjectTypes.ASSIGNMENT, section: this.FILTER_SECTION }));
    }

    onStoriesFilterSave() {
        this.store.dispatch(InlineFilterActions.saveFilterState({ objectType: FilterObjectTypes.STORY, section: this.FILTER_SECTION }));
    }

    onAssignmentsFilterRevert() {
        this.store.dispatch(InlineFilterActions.revertFilterState({ objectType: FilterObjectTypes.ASSIGNMENT, section: this.FILTER_SECTION }));
    }

    onStoriesFilterRevert() {
        this.store.dispatch(InlineFilterActions.revertFilterState({ objectType: FilterObjectTypes.STORY, section: this.FILTER_SECTION }));
    }

    onAssignmentsFilterClear() {
        this.store.dispatch(InlineFilterActions.clearFilterState({ objectType: FilterObjectTypes.ASSIGNMENT, section: this.FILTER_SECTION }));
    }

    onStoriesFilterClear() {
        this.store.dispatch(InlineFilterActions.clearFilterState({ objectType: FilterObjectTypes.STORY, section: this.FILTER_SECTION }));
    }

    closeModal() {
        this.store.dispatch(OpenFullscreenModalActions.closeFullscreenModal());
    }

    onDragStart(event: Event, story: StorytellingStoryRecord) {
        const dragEvent = event as DragEvent;
        dragEvent.dataTransfer!.setData(
            DragEventKeys.DRAG_STORY,
            JSON.stringify({
                scriptDatabase: story.Database,
                scriptPath: story.Path?.fullPath,
                scriptGuid: story.Guid,
                actionType: GridDragTypes.ADD.toString(),
                text: story.text,
                textCommands: story.TextCommands,
                forceReloadStory: true,
            })
        );
        dragEvent.dataTransfer!.effectAllowed = 'copyMove';
    }

    timezoneChanged(fieldId: string, event: any) {
        this.data.update(val => {
            val[fieldId] = event;
            return val;
        });
        this.store.dispatch(TopicRecordActions.updateTopics({ records: [this.data()] }));
    }

    setAssignmentsCondensedLayout(isCondensed: boolean) {
        this.store.dispatch(FullscreenActions.setItemLayoutState({ state: { assignmentsFullscreen: isCondensed } }));
    }

    setStoriesCondensedLayout(isCondensed: boolean) {
        this.store.dispatch(FullscreenActions.setItemLayoutState({ state: { storiesFullscreen: isCondensed } }));
    }
}
